import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ClipboardCheck, Waves, TrendingUp, Check, ArrowRight, Thermometer, Armchair, Award, Sparkles, Lock, Headset, MailCheck, CalendarDays } from "lucide-react";
import Navbar from "@/components/site/Navbar";
import { Seo } from "@/components/site/Seo";
import Footer from "@/components/site/Footer";
import FinalCTA from "@/components/site/FinalCTA";
import WhereNext from "@/components/site/WhereNext";

const nextItems = [
  { sub: "Explore our lessons", label: "Our Swimming Lessons", to: "/classes" },
  { sub: "Start with baby swimming", label: "Baby Swimming Lessons", to: "/classes/baby-swimming" },
  { sub: "Check class availability", label: "Weekly Timetable", to: "/timetable" },
  { sub: "Speak to our team", label: "Contact Us", to: "/contact" },
];

const facilities = [
  { icon: <Thermometer size={22} />, accent: "#4FA3C6", title: "Warm 31–33°C water", desc: "Cosy, baby-friendly pool temperatures so little ones stay comfortable and happy." },
  { icon: <Armchair size={22} />, accent: "#FF8C6B", title: "Spectator platform", desc: "Relax on our dedicated viewing area and watch every splash and smile up close." },
  { icon: <Award size={22} />, accent: "#F59E0B", title: "Milestone awards", desc: "We celebrate every achievement with awards that mark your child's progression." },
  { icon: <Sparkles size={22} />, accent: "#6BAFCC", title: "Spotless facilities", desc: "The cleanest pool centre you'll ever find — spotless from poolside to changing rooms." },
];

const phases = [
  {
    tag: "Before your first lesson",
    icon: <ClipboardCheck size={24} />,
    accent: "#0EA5E9",
    intro: "We know starting swimming lessons can feel daunting — especially if your child is nervous or new to the water. Here's how we make it easier:",
    points: [
      "Happiness Heroes on hand to greet and guide you the moment you arrive",
      "Clear guidance before you arrive",
      "Friendly, welcoming instructors",
      "Calm introductions to the pool",
      "Time for children to settle in",
    ],
  },
  {
    tag: "During the lesson",
    icon: <Waves size={24} />,
    accent: "#FF7A1A",
    intro: "We follow a gentle, baby-led approach — every child moves at their own individual pace, never rushed, within a consistent structure so they always know what to expect.",
    points: [
      "Baby-led — we follow your child's cues",
      "Progress at each child's individual pace",
      "Small groups",
      "Clear safety boundaries",
      "Play-based learning",
      "Positive encouragement throughout",
    ],
    tagline: "No pressure. No forcing. Just steady, supportive progress.",
  },
  {
    tag: "After the lesson",
    icon: <TrendingUp size={24} />,
    accent: "#10B981",
    intro: "You'll see confidence building lesson by lesson — supported by clear progression and instructor feedback.",
    points: [],
    tagline: "From nervous to confident — one step at a time.",
  },
];

export default function HowItWorks() {
  return (
    <div className="min-h-screen bg-[#F8FAFC] text-[#334155] font-body" data-testid="how-it-works-page">
      <Seo
        title="What to Expect — Your First Swim Lesson | Happy Tiddlers"
        description="From your first visit to seeing your child's confidence grow — here's exactly what to expect at Happy Tiddlers Swim School in Barton-le-Clay."
        path="/how-it-works"
      />
      <Navbar />

      {/* Header */}
      <section className="relative overflow-hidden pt-16 pb-16 md:pt-24 md:pb-20" data-testid="hiw-header">
        <div className="blob absolute -top-32 -left-32 w-[520px] h-[520px] rounded-full bg-[#0EA5E9]/10" />
        <div className="container mx-auto px-6 md:px-12 relative max-w-3xl">
          <span className="text-sm text-[#0EA5E9] tracking-[0.2em] uppercase font-bold">What to expect</span>
          <h1 className="mt-4 font-heading text-4xl md:text-5xl lg:text-6xl font-black tracking-tighter text-[#0F172A] leading-[1.05]">
            From nervous to confident — one step at a time
          </h1>
          <p className="mt-6 text-lg text-[#334155] leading-relaxed">
            Here's exactly how it works, from your very first visit through to seeing your child's
            confidence grow — so there are no surprises, only support.
          </p>
        </div>
      </section>

      {/* Timeline */}
      <section className="pb-16 md:pb-24" data-testid="hiw-timeline">
        <div className="container mx-auto px-6 md:px-12">
          <div className="relative max-w-4xl mx-auto">
            {/* vertical connector */}
            <div className="absolute left-[27px] md:left-[31px] top-4 bottom-4 w-0.5 bg-slate-200" aria-hidden="true" />

            <div className="space-y-8 md:space-y-10">
              {phases.map((p, i) => (
                <motion.div
                  key={p.tag}
                  initial={{ opacity: 0, y: 28 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.6, delay: i * 0.1 }}
                  className="relative flex gap-6 md:gap-8"
                  data-testid={`hiw-phase-${i}`}
                >
                  <div className="relative z-10 shrink-0 self-start">
                    <span className="grid place-items-center w-14 h-14 md:w-16 md:h-16 rounded-2xl text-white shadow-lg" style={{ backgroundColor: p.accent }}>
                      {p.icon}
                    </span>
                    <span
                      className="absolute -bottom-2 -right-2 grid place-items-center w-6 h-6 rounded-full bg-white text-[11px] font-heading font-black shadow-md ring-2 ring-white"
                      style={{ color: p.accent }}
                      aria-hidden="true"
                    >
                      {i + 1}
                    </span>
                  </div>

                  <div className="flex-1 bg-white rounded-[2rem] p-8 md:p-10 border border-slate-100 shadow-sm">
                    <span className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.2em] uppercase mb-3" style={{ color: p.accent }}>
                      Step {i + 1} of {phases.length}
                      <span className="hidden sm:flex items-center gap-1">
                        {phases.map((ph, d) => (
                          <span
                            key={ph.tag}
                            className="w-1.5 h-1.5 rounded-full"
                            style={{ backgroundColor: d <= i ? p.accent : "#E2E8F0" }}
                          />
                        ))}
                      </span>
                    </span>
                    <h2 className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-[#0F172A]">{p.tag}</h2>
                    <p className="mt-4 text-lg text-[#334155] leading-relaxed">{p.intro}</p>

                    {p.points.length > 0 && (
                      <ul className="mt-6 grid sm:grid-cols-2 gap-3">
                        {p.points.map((pt) => (
                          <li key={pt} className="flex items-start gap-3">
                            <span className="grid place-items-center w-6 h-6 mt-0.5 rounded-full text-white shrink-0" style={{ backgroundColor: p.accent }}>
                              <Check size={14} strokeWidth={3} />
                            </span>
                            <span className="text-[#334155]">{pt}</span>
                          </li>
                        ))}
                      </ul>
                    )}

                    {p.tagline && (
                      <p className="mt-6 font-heading text-xl font-semibold text-[#0A2540]">{p.tagline}</p>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          <div className="mt-14 flex flex-wrap items-center justify-center gap-3">
            <a
              href="https://portal.pivot1st.co.uk/book/2/class"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#FF7A1A] text-white font-bold px-8 py-4 rounded-full shadow-[0_8px_20px_-6px_rgba(255,122,26,0.6)] hover:-translate-y-1 transition-all duration-300"
              data-testid="hiw-book-cta"
            >
              Book now <ArrowRight size={18} />
            </a>
            <Link
              to="/timetable"
              className="inline-flex items-center gap-2 bg-[#0A2540] text-white font-bold px-8 py-4 rounded-full shadow-[0_8px_20px_-6px_rgba(10,37,64,0.5)] hover:-translate-y-1 transition-all duration-300"
              data-testid="hiw-timetable-cta"
            >
              <CalendarDays size={18} /> View the timetable
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 text-[#0A2540] font-bold px-7 py-4 rounded-full border-2 border-[#0A2540] hover:bg-[#0A2540] hover:text-white transition-all duration-300"
              data-testid="hiw-get-started-cta"
            >
              Get help choosing a class <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Facilities */}
      <section className="pb-16 md:pb-24" data-testid="hiw-facilities">
        <div className="container mx-auto px-6 md:px-12">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <span className="text-sm text-[#0EA5E9] tracking-[0.2em] uppercase font-bold">Our centre</span>
            <h2 className="mt-4 font-heading text-3xl md:text-4xl font-black tracking-tight text-[#0F172A]">
              Little touches that make a big difference
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {facilities.map((f, i) => (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="group bg-white rounded-[1.75rem] p-7 border border-slate-100 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
                data-testid={`hiw-facility-${i}`}
              >
                <span className="grid place-items-center w-12 h-12 rounded-2xl text-white shadow-md mb-5 group-hover:scale-110 transition-transform duration-300" style={{ backgroundColor: f.accent }}>
                  {f.icon}
                </span>
                <h3 className="font-heading text-lg font-bold text-[#0F172A]">{f.title}</h3>
                <p className="mt-2 text-[#64748B] leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Support that sets us apart */}
      <section className="pb-16 md:pb-24" data-testid="hiw-support">
        <div className="container mx-auto px-6 md:px-12">
          <div className="relative overflow-hidden bg-[#0A2540] rounded-[2rem] p-8 md:p-14">
            <div className="blob absolute -top-24 -right-24 w-80 h-80 rounded-full bg-[#0EA5E9]/20" aria-hidden="true" />
            <div className="blob absolute -bottom-28 -left-20 w-80 h-80 rounded-full bg-[#FF7A1A]/15" aria-hidden="true" />
            <div className="relative">
              <span className="text-sm text-[#38BDF8] tracking-[0.2em] uppercase font-bold">What sets us apart</span>
              <h2 className="mt-4 font-heading text-2xl md:text-3xl lg:text-4xl font-bold tracking-tight text-white leading-tight max-w-2xl">
                Looked after, from the moment you arrive
              </h2>
              <p className="mt-4 text-[#CBD5E1] max-w-2xl">
                Most swim schools disappear once the lesson starts. We don't. Ours is a private
                centre with a friendly team on hand every step of the way.
              </p>
              <div className="mt-10 grid sm:grid-cols-3 gap-6 md:gap-8">
                {[
                  { icon: <Lock size={22} />, accent: "#1F7A8C", title: "Private & exclusive", desc: "Our own pool — no public onlookers, no crowds. Just a calm, safe space for your little swimmer." },
                  { icon: <Headset size={22} />, accent: "#F59E0B", title: "Reception team on hand", desc: "A real, friendly team at the centre during lesson times — something you simply won't find elsewhere." },
                  { icon: <MailCheck size={22} />, accent: "#6ED3C3", title: "Fast, friendly replies", desc: "Questions or changes? Our support team responds to emails and queries quickly — no waiting around." },
                ].map((s, i) => (
                  <motion.div
                    key={s.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: i * 0.08 }}
                    className="flex flex-col gap-4"
                    data-testid={`hiw-support-${i}`}
                  >
                    <span className="grid place-items-center w-12 h-12 rounded-2xl text-white shrink-0" style={{ backgroundColor: s.accent }}>
                      {s.icon}
                    </span>
                    <h3 className="font-heading text-lg font-bold text-white leading-snug">{s.title}</h3>
                    <p className="text-[#CBD5E1] leading-relaxed">{s.desc}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <FinalCTA />
      <WhereNext items={nextItems} />
      <Footer />
    </div>
  );
}
